import { format } from "date-fns";
import { calendarBooking } from "../types/calendarBooking";
import { EmailRequestDTO } from "../types/email";
import { toLocalISOString } from "../utils/general";
import { bookingService } from "./bookingService";
import { Column } from "./exportToExcel";
import { TimezoneService } from "./timezoneUtils";

interface EmailOptions {
  dateRange?: {
    startDate: Date | null;
    endDate: Date | null;
  };
  mtdClosings?: number;
  isUserSelection?: boolean; // True if user has selected specific bookings
}

export class EmailService {
  private static generateSubjectDateRange(options?: EmailOptions): string {
    // User selection takes precedence over date range
    if (options?.isUserSelection) {
      return "User Selection";
    }

    // If a date range is selected, show the date range
    if (options?.dateRange?.startDate || options?.dateRange?.endDate) {
      const { startDate, endDate } = options.dateRange;

      if (startDate && endDate) {
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();

        // If years are different, show year on both dates
        if (startYear !== endYear) {
          const startFormatted = format(startDate, "MMM d, yyyy");
          const endFormatted = format(endDate, "MMM d, yyyy");
          return `${startFormatted} - ${endFormatted}`;
        } else {
          // Same year, only show year on end date
          const startFormatted = format(startDate, "MMM d");
          const endFormatted = format(endDate, "MMM d, yyyy");
          return `${startFormatted} - ${endFormatted}`;
        }
      } else if (startDate) {
        return `From ${format(startDate, "MMM d, yyyy")}`;
      } else if (endDate) {
        return `Until ${format(endDate, "MMM d, yyyy")}`;
      }
    }

    // No date range: user is viewing all data or selected specific bookings
    return "User Selection";
  }

  private static generateDateRangeText(options?: EmailOptions): string {
    // User selection takes precedence over date range
    if (options?.isUserSelection) {
      return "User Selection";
    }

    if (!options?.dateRange?.startDate && !options?.dateRange?.endDate) {
      return "User Selection";
    }

    const { startDate, endDate } = options.dateRange;

    if (startDate && endDate) {
      const startYear = startDate.getFullYear();
      const endYear = endDate.getFullYear();

      // If years are different, show year on both dates
      if (startYear !== endYear) {
        const startFormatted = format(startDate, "MMMM d, yyyy");
        const endFormatted = format(endDate, "MMMM d, yyyy");
        return `${startFormatted} to ${endFormatted}`;
      } else {
        // Same year, only show year on end date
        const startFormatted = format(startDate, "MMMM d");
        const endFormatted = format(endDate, "MMMM d, yyyy");
        return `${startFormatted} to ${endFormatted}`;
      }
    } else if (startDate) {
      return `From ${format(startDate, "MMMM d, yyyy")}`;
    } else if (endDate) {
      return `Until ${format(endDate, "MMMM d, yyyy")}`;
    }

    return "User Selection";
  }

  /**
   * Option 1: Create EML file (Best for Outlook/Thunderbird)
   * Opens directly in user's default email client
   */
  static createEMLFile(
    bookings: calendarBooking[],
    columns: Column[],
    recipientEmail?: string,
    options?: EmailOptions
  ) {
    const subjectDateRange = this.generateSubjectDateRange(options);
    const subject = `Bookings Report - ${bookings.length} items (${subjectDateRange})`;
    const htmlBody = this.generateHTMLTableForEmail(bookings, columns, options);
    const textBody = this.generatePlainTextTable(bookings, columns, options);

    const emlContent = `To: ${recipientEmail || ""}
Subject: ${subject}
MIME-Version: 1.0
Content-Type: multipart/alternative; boundary="boundary123"

--boundary123
Content-Type: text/plain; charset=utf-8

${textBody}

--boundary123
Content-Type: text/html; charset=utf-8

${htmlBody}

--boundary123--`;

    // Create and download EML file
    const blob = new Blob([emlContent], { type: "message/rfc822" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `bookings-report-${
      toLocalISOString(new Date()).split("T")[0]
    }.eml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Option 2: Open default email client with mailto (Limited by URL length)
   * Good for simple cases
   */
  static openDefaultEmailClient(
    bookings: calendarBooking[],
    columns: Column[],
    recipientEmail?: string,
    options?: EmailOptions
  ) {
    const subject = `Bookings Report - ${bookings.length} items`;
    const body = this.generatePlainTextTable(bookings, columns, options);

    // URL encode the content
    const mailtoUrl = `mailto:${
      recipientEmail || ""
    }?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    // Check URL length limit (most browsers limit to ~2000 characters)
    if (mailtoUrl.length > 2000) {
      alert(
        "Too much data for email link. Please use the EML file option instead."
      );
      return false;
    }

    window.location.href = mailtoUrl;
    return true;
  }

  static async sendEmailWithRecipient(
    bookings: calendarBooking[],
    columns: Column[],
    recipientEmail?: string[],
    options?: EmailOptions,
    message?:string
  ) {
    const subjectDateRange = this.generateSubjectDateRange(options);
    const subject = `Bookings Report - ${bookings.length} items (${subjectDateRange})`;

    const htmlBody = `${message ? `<p style="font-size:16px; margin-bottom:20px;">${message}</p>` : ''} ${this.generateHTMLTableForEmail(bookings, columns, options)}`;


    try {
      const emailRequest: EmailRequestDTO = {
        To: recipientEmail,
        Subject: subject,
        Body: htmlBody,
        IsHtml: true, // This ensures the HTML table is rendered properly
      };

      const response = await bookingService.sendEmail(emailRequest);
    } catch (error) {
      console.error("❌ Error sending email:", error);
      alert("Error sending email. Please check your connection and try again.");
    }
  }

  /**
   * Option 3: Copy formatted data to clipboard
   * User can paste into any email client
   */
  static async copyToClipboard(
    bookings: calendarBooking[],
    columns: Column[],
    options?: EmailOptions
  ) {
    const htmlTable = this.generateHTMLTableForEmail(
      bookings,
      columns,
      options
    );
    const plainText = this.generatePlainTextTable(bookings, columns, options);

    try {
      const clipboardItem = new ClipboardItem({
        "text/html": new Blob([htmlTable], { type: "text/html" }),
        "text/plain": new Blob([plainText], { type: "text/plain" }),
      });

      await navigator.clipboard.write([clipboardItem]);
      return true;
    } catch (error) {
      // Fallback to plain text only
      try {
        await navigator.clipboard.writeText(plainText);
        return true;
      } catch (fallbackError) {
        console.error("Failed to copy to clipboard:", fallbackError);
        return false;
      }
    }
  }

  private static generateHTMLTableForEmail(
    bookings: calendarBooking[],
    columns: Column[],
    options?: EmailOptions
  ): string {
    const userTimezone = TimezoneService.getUserTimezoneDisplay();
    const dateRangeText = this.generateDateRangeText(options);
    const mtdClosings = options?.mtdClosings ?? 0;
    const currentMonth = format(new Date(), "MMMM yyyy");

    const tableRows = bookings
      .map((booking, index) => {
        const rowBgColor = index % 2 === 0 ? "#fafafa" : "#ffffff";
        const cells = columns
          .map((column) => {
            let value = this.getCellValue(booking, column);
            if (column.label === "Status") {
              const colorMap: any = {
                Upcoming: "#e53935",
                "In Progress": "#fb8c00",
                Completed: "#1e88e5",
                Default: "#9e9e9e",
              };
              const bgColor = colorMap[value] || colorMap["Default"];
              return `<td style="border:1px solid #e0e0e0; padding:14px; text-align:center; font-size:14px;"><span style="background:${bgColor};color:white;padding:6px 14px;border-radius:20px;display:inline-block;min-width:90px;font-weight:600;">${value}</span></td>`;
            }
            return `<td style="border:1px solid #e0e0e0; padding:14px; font-size:14px; vertical-align:top;">${value}</td>`;
          })
          .join("");
        return `<tr style="background-color:${rowBgColor};">${cells}</tr>`;
      })
      .join("");

    const headerCells = columns
      .map(
        (column) =>
          `<th style="border:1px solid #e0e0e0; padding:16px; background:#1976d2; color:white; font-weight:600; font-size:15px; text-align:left;">${column.label}</th>`
      )
      .join("");
    return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  </head>
  <body style="margin:0; padding:0; font-family: 'Segoe UI', Arial, sans-serif; background: #f4f6f8; color:#333;">
    <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f4f6f8;">
      <tr>
        <td align="center">
          <table width="600" cellpadding="0" cellspacing="0" border="0" style="width:100%; margin:0; background:#fff;">
            <tr>
              <td style="background-color: #1976d2; color:white; padding:30px; text-align:center;">
                <h1 style="margin:0; color:white;">📊 FNTIS Bookings Report</h1>
              </td>
            </tr>
            <tr>
              <td style="padding:20px; background:#e3f2fd; border-bottom:1px solid #ddd;">
                 <table width="100%" cellpadding="0" cellspacing="0" border="0">
                  <tr>
                    <td align="center" style="text-align:center; padding: 0 8px;">
                      <div style="font-size:24px; font-weight:bold; color:#1976d2;">${
                        bookings.length
                      }</div>
                      <div style="font-size:12px;">Total Records</div>
                    </td>
                    <td align="center" style="text-align:center; padding: 0 8px;">
                      <div style="font-size:24px; font-weight:bold; color:#1976d2;">${
                        bookings.filter((b) => b.status === "upcoming").length
                      }</div>
                      <div style="font-size:12px;">Upcoming</div>
                    </td>
                    <td align="center" style="text-align:center; padding: 0 8px;">
                      <div style="font-size:24px; font-weight:bold; color:#1976d2;">${
                        bookings.filter((b) => b.status === "inProgress").length
                      }</div>
                      <div style="font-size:12px;">In Progress</div>
                    </td>
                    <td align="center" style="text-align:center; padding: 0 8px;">
                      <div style="font-size:24px; font-weight:bold; color:#1976d2;">${
                        bookings.filter((b) => b.status === "completed").length
                      }</div>
                      <div style="font-size:12px;">Completed</div>
                    </td>
                    <td align="center" style="text-align:center; padding: 0 8px;">
                      <div style="font-size:24px; font-weight:bold; color:#1976d2;">${mtdClosings}</div>
                      <div style="font-size:12px;">MtD Closings</div>
                      <div style="font-size:10px; color:#1976d2;">(${currentMonth})</div>
                    </td>
                  </tr>
                </table>
                <div style="text-align:center; margin-top:15px; padding:10px; background:rgba(25,118,210,0.1); border-radius:8px;">
                  <div style="font-weight:bold; color:#1976d2; margin-bottom:5px;">📅 Report Date Range</div>
                  <div style="font-size:14px; color:#333;">${dateRangeText}</div>
                </div>
              </td>
            </tr>
            <tr>
              <td style="width:100%;">
                <table style="width: 100%; border-collapse:collapse;" cellpadding="5" cellspacing="0" border="0">
                  <thead>
                    <tr>
                      ${headerCells}
                    </tr>
                  </thead>
                  <tbody>
                    ${tableRows}
                  </tbody>
                </table>
              </td>
            </tr>
            <tr>
              <td style="text-align:center; padding:20px; color:#777; font-size:14px;">
                <div>Report generated in timezone: ${userTimezone}</div>
                <div style="margin-top:8px; font-size:12px;">
                  📈 MtD Closings: ${mtdClosings} • 📊 Date Range: ${dateRangeText}
                </div>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;
  }

  static generatePrintablePDF(
    bookings: calendarBooking[],
    columns: Column[],
    options?: EmailOptions
  ) {
    const htmlContent = this.generateHTMLTableForEmail(
      bookings,
      columns,
      options
    );

    const printWindow = window.open("", "_blank", "width=800,height=600");

    if (printWindow) {
      printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>FNTIS Bookings Report - ${new Date().toLocaleDateString()}</title>
          <style>
            @media print {
              body { margin: 0; }
              .no-print { display: none !important; }
            }
            @media screen {
              body { 
                padding: 20px; 
                font-family: 'Segoe UI', Arial, sans-serif;
              }
              .print-instructions {
                background: #e3f2fd;
                padding: 15px;
                border-radius: 8px;
                margin-bottom: 20px;
                border-left: 4px solid #1976d2;
              }
              .print-button {
                background: #1976d2;
                color: white;
                border: none;
                padding: 10px 20px;
                border-radius: 4px;
                cursor: pointer;
                font-size: 14px;
                margin-right: 10px;
              }
              .print-button:hover {
                background: #1565c0;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-instructions no-print">
            <h3>📄 Print Instructions</h3>
            <p>Click "Print Report" to print or save as PDF. Use your browser's print dialog to:</p>
            <ul>
              <li><strong>Print:</strong> Select your printer and click Print</li>
              <li><strong>Save as PDF:</strong> Choose "Save as PDF" in the destination dropdown</li>
            </ul>
            <button class="print-button" onclick="window.print()">🖨️ Print Report</button>
            <button class="print-button" onclick="window.close()">❌ Close</button>
          </div>
          ${htmlContent}
          <script>
            window.focus();
            
            // setTimeout(() => { window.print(); }, 1000);
          </script>
        </body>
      </html>
    `);

      printWindow.document.close();
    } else {
      alert(
        "Pop-up blocked. Please allow pop-ups for this site to use the print feature."
      );
    }
  }

  /**
   * Generate plain text table for email body - Enhanced formatting
   */
  private static generatePlainTextTable(
    bookings: calendarBooking[],
    columns: Column[],
    options?: EmailOptions
  ): string {
    const userTimezone = TimezoneService.getUserTimezoneDisplay();
    const separator = "=".repeat(100);
    const lineSeparator = "-".repeat(100);

    const dateRangeText = this.generateDateRangeText(options);
    const mtdClosings = options?.mtdClosings ?? 0;
    const currentMonth = format(new Date(), "MMMM yyyy");

    const header = `
${separator}
📊 FNTIS BOOKINGS REPORT - FULL PAGE VIEW
${separator}

📅 Generated: ${new Date().toLocaleString()}
📊 Total Records: ${bookings.length}
📍 Timezone: ${userTimezone} (converted from Eastern Time)
📈 Month-to-Date Closings: ${mtdClosings} (${currentMonth})
📅 Date Range: ${dateRangeText}

📈 Summary Statistics:
   • Upcoming Appointments: ${
     bookings.filter((b) => b.status === "upcoming").length
   }
   • In Progress: ${bookings.filter((b) => b.status === "inProgress").length}  
   • Completed: ${bookings.filter((b) => b.status === "completed").length}
   • Other Status: ${
     bookings.filter(
       (b) => !["upcoming", "inProgress", "completed"].includes(b.status || "")
     ).length
   }
   • Month-to-Date Closings: ${mtdClosings} (${currentMonth})

📋 REPORT FILTERS:
   • Date Range Applied: ${dateRangeText}
   • Records Matching Criteria: ${bookings.length}

${separator}
DETAILED BOOKING RECORDS
${separator}`;

    const tableData = bookings
      .map((booking, index) => {
        const bookingData = columns
          .map((column) => {
            const value = this.getCellValue(booking, column);
            return `${column.label}: ${value}`;
          })
          .join("\n   ");

        return `
📋 Record ${index + 1} of ${bookings.length}:
   ${bookingData}
${lineSeparator}`;
      })
      .join("");

    const footer = `
${separator}
📧 FNTIS BOOKING MANAGEMENT SYSTEM - ENHANCED REPORT
${separator}
📈 Month-to-Date Performance: ${mtdClosings} closings in ${currentMonth}
📅 Report Coverage: ${dateRangeText}
🌍 All times converted to your timezone: ${userTimezone}
📞 For support, contact your system administrator
🏢 First National Title & Insurance Services, Inc.
${separator}`;

    return `${header}${tableData}${footer}`;
  }

  /**
   * Extract cell value based on column configuration
   */
  private static getCellValue(
    booking: calendarBooking,
    column: Column
  ): string {
    const id = column.id as string;
    let value: any;

    if (id === "customerName" && column.label === "Borrower") {
      if (booking.loanData) {
        const fullName = `${booking.loanData.borrowerFirstName || ""} ${
          booking.loanData.borrowerLastName || ""
        }`.trim();
        value = fullName || booking.customerName;
      } else {
        value = booking.customerName;
      }
    } else if (id === "LoanPurpose" && column.label === "Loan Purpose") {
      value = booking.loanData?.loanPurpose;
    } else if (id === "LoanCloser" && column.label === "Loan Closer") {
      value = booking.loanData?.loanCloser || booking.LoanCloser;
    } else if (id === "LoanOfficer" && column.label === "Loan Officer") {
      value = booking.loanData?.loanOfficer || booking.LoanOfficer;
    } else if (id === "dpa" && column.label === "DPA Program") {
      value = booking.loanData?.dpa || booking.dpa || "";
    } else if (id === "start" && column.label === "Closing Date") {
      if (booking.start?.dateTime) {
        value = TimezoneService.formatDateForUser(
          booking.start.dateTime,
          "MMMM d, yyyy"
        );
      }
    } else if (id === "start" && column.label === "Closing Time") {
      if (booking.start?.dateTime) {
        value = TimezoneService.formatTimeForUser(
          booking.start.dateTime,
          "h:mm a"
        );
      }
    } else if (id === "serviceName" && column.label === "Service Location") {
      value = booking.serviceName;
    } else if (id === "status") {
      if (booking.status) {
        switch (booking.status) {
          case "upcoming":
            value = "Upcoming";
            break;
          case "inProgress":
            value = "In Progress";
            break;
          case "completed":
            value = "Completed";
            break;
          case "canceled":
            value = "Canceled";
            break;
          default:
            value = booking.status;
        }
      }
    } else {
      value = booking[id as keyof calendarBooking];
    }

    return value || "-";
  }
}
